import { httpGet, httpPatch, httpPut } from '@/api/http';
import { V2_CALL_CENTER_AGENTS } from '@/constants/endpoint';
import { computed, type MaybeRefOrGetter, ref, toRef } from 'vue';
import { useAuthenticatedQuery } from '@/utils/query';
import { type AgentDTO, type AgentResource, AgentState, AgentStatus } from '@/data/Agent';
import { normalizeArrayToMapByKey } from '@/utils/dataTransformationUtils';
import { formatDurationAsMinutesAndSeconds, getDateInSeconds, getNowInSeconds } from '@/utils/dateUtils';
import { getAxiosErrorMessage } from '@/api/axios';
import type { AxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { i18n } from '@/plugins/i18n';
import { useNotificationStore } from '@/stores/useNotificationStore';
import { useTimer } from '@/composables/useTimer';
import { useI18n } from 'vue-i18n';

export const AGENTS_QUERY_KEY = 'agents';

interface SetAgentIdleParams {
  agentUuid: string;
  downtimeEnd?: number | null;
}

export const logoutAgent = (agentUuid: string) => {
  return httpPatch(`${V2_CALL_CENTER_AGENTS}/${agentUuid}/status`, { status: AgentStatus.LOGGED_OUT });
};

export const useAgents = (refetchEnabled = ref(false), refetchInterval: number | false = 5_000) => {
  const { t } = useI18n();
  const {
    data: _agents,
    refetch: refetchAgents,
    status,
  } = useAuthenticatedQuery({
    queryKey: [AGENTS_QUERY_KEY],
    queryFn: (): Promise<Map<string, AgentDTO>> => {
      return httpGet<AgentResource[]>(V2_CALL_CENTER_AGENTS)
        .then((agents) =>
          agents.map((agent, index) => ({
            ...agent,
            name: agent.name || `${t('common.agent')} ${index + 1}`,
            status: agent.status,
            lastStatusChange: getDateInSeconds(agent.lastStatusChange),
            lastBridgeStart: getDateInSeconds(agent.lastBridgeStart),
            downtimeBegin: getDateInSeconds(agent.downtimeBegin),
            downtimeEnd: getDateInSeconds(agent.downtimeEnd),
          }))
        )
        .then((agents) => normalizeArrayToMapByKey(agents, 'uuid'));
    },
    refetchInterval,
    refetchOnMount: false,
    enabled: refetchEnabled,
  });

  return { _agents, refetchAgents, status };
};

export const useEndDowntimeMutation = () => {
  const queryClient = useQueryClient();
  const { setSnackBar } = useNotificationStore();
  const { t } = i18n.global;

  return useMutation({
    mutationFn: ({ agentUuid }: { agentUuid: string }) => httpPut(`${V2_CALL_CENTER_AGENTS}/${agentUuid}/downtime/end`),
    onMutate: async ({ agentUuid }) => {
      await queryClient.cancelQueries({ queryKey: [AGENTS_QUERY_KEY] });
      const previousData = queryClient.getQueryData<Map<string, AgentDTO>>([AGENTS_QUERY_KEY]);

      const agentsMutation = (agents: Map<string, AgentDTO> | undefined) => {
        if (!agents) return agents;

        const updatedAgents = new Map(agents);
        const agent = updatedAgents.get(agentUuid);

        if (agent) {
          updatedAgents.set(agentUuid, {
            ...agent,
            state: AgentState.WAITING,
            downtimeBegin: null,
            downtimeEnd: getNowInSeconds(),
            downtimeReason: null,
          });
        }

        return updatedAgents;
      };

      queryClient.setQueryData<Map<string, AgentDTO>>([AGENTS_QUERY_KEY], agentsMutation);

      return { previousData };
    },

    onSuccess: async () => {
      setSnackBar({
        message: t('call-center.agent-activity.downtime-end-success'),
        variant: 'success',
      });
    },

    onError: async (error: AxiosError) => {
      setSnackBar({
        message: getAxiosErrorMessage(error) ?? t('call-center.agent-activity.downtime-end-error'),
        variant: 'error',
      });
    },
  });
};

export const useSetAgentIdleMutation = () => {
  const queryClient = useQueryClient();
  const { setSnackBar } = useNotificationStore();
  const { t } = i18n.global;

  return useMutation<void, AxiosError, SetAgentIdleParams>({
    mutationFn: () => Promise.resolve(),
    onMutate: async ({ agentUuid, downtimeEnd }) => {
      await queryClient.cancelQueries({ queryKey: [AGENTS_QUERY_KEY] });
      const previousData = queryClient.getQueryData<Map<string, AgentDTO>>([AGENTS_QUERY_KEY]);

      const agentsMutation = (agents: Map<string, AgentDTO> | undefined) => {
        if (!agents) return agents;

        const updatedAgents = new Map(agents);
        const agent = updatedAgents.get(agentUuid);

        if (agent) {
          updatedAgents.set(agentUuid, {
            ...agent,
            state: AgentState.IDLE,
            downtimeBegin: getNowInSeconds(),
            downtimeEnd: downtimeEnd ?? null,
            downtimeReason: 'WRAP_UP',
          });
        }
        return updatedAgents;
      };

      queryClient.setQueryData<Map<string, AgentDTO>>([AGENTS_QUERY_KEY], agentsMutation);
      return { previousData };
    },

    onSuccess: async () => {
      setSnackBar({
        message: t('call-center.agent-activity.wrapup-active'),
        variant: 'info',
        timeout: 7000,
      });
    },
  });
};

export function useWrapUpTime(agent?: MaybeRefOrGetter<AgentDTO | undefined>) {
  const _agent = toRef(agent);
  const { currentTime } = useTimer();

  const _wrapUpTime = computed(() => {
    if (_agent.value?.downtimeReason !== 'WRAP_UP') return null;

    const now = Math.floor(currentTime.value / 1_000);
    const { downtimeEnd, downtimeBegin } = _agent.value;

    // Automatic mode: countdown (remaining time)
    if (downtimeEnd) {
      const remaining = downtimeEnd - now;
      return remaining > 0 ? remaining : null;
    }

    // Manual mode: countup (elapsed time)
    if (!downtimeBegin || downtimeBegin > now) return null;
    return now - downtimeBegin;
  });

  const _formattedWrapUpTime = computed(() => {
    if (_wrapUpTime.value === null) return '';
    return formatDurationAsMinutesAndSeconds(_wrapUpTime.value);
  });

  return {
    _formattedWrapUpTime,
  };
}
